import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "@/context/AdminContext";
import { useAdminStorage } from "@/hooks/useAdminStorage";
import { loadCustomPages, saveCustomPage, type CustomPage } from "@/utils/customPageStore";

const STANDALONE_PAGES: { path: string; label: string; icon: string; visibilityKey?: string; alwaysVisible?: boolean }[] = [
  { path: "/", label: "Home Page", icon: "ri-home-line", alwaysVisible: true },
  { path: "/artists", label: "Our Artists", icon: "ri-palette-line", alwaysVisible: true },
  { path: "/careers", label: "Careers", icon: "ri-briefcase-line", alwaysVisible: true },
  { path: "/testimonials", label: "Testimonials / Reviews", icon: "ri-star-line", visibilityKey: "ma_testimonials_v2" },
  { path: "/leave-a-review", label: "Leave a Review", icon: "ri-chat-heart-line", visibilityKey: "ma_leave_review_v3" },
  { path: "/admin", label: "Admin Center", icon: "ri-settings-3-line", alwaysVisible: true },
];

function usePageVisibility(key: string): [boolean, (v: boolean) => void] {
  // Defensive: old code may have stored raw booleans in localStorage.
  // Use a safe default object and coerce parsed values into the expected shape.
  const [meta, setMeta] = useAdminStorage<Record<string, unknown>>(key, { pageVisible: false });

  let visible = false;
  try {
    if (typeof meta === "object" && meta !== null && "pageVisible" in meta) {
      visible = meta.pageVisible === true;
    }
  } catch { /* ignore */ }

  const setVisible = useCallback((v: boolean) => {
    try {
      setMeta({ pageVisible: v });
    } catch { /* ignore */ }
  }, [setMeta]);

  return [visible, setVisible];
}

export default function SiteDirectoryTab({ isDark }: { isDark: boolean }) {
  const { sectionOrder, isSectionVisible, toggleSection } = useAdmin();
  const navigate = useNavigate();
  const headingColor = isDark ? "text-white" : "text-gray-800";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const cardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";
  const rowHover = isDark ? "hover:bg-gray-700/30" : "hover:bg-gray-50";
  const divider = isDark ? "border-gray-700" : "border-gray-100";

  const [testimonialsVisible, setTestimonialsVisible] = usePageVisibility("ma_testimonials_v2");
  const [leaveReviewVisible, setLeaveReviewVisible] = usePageVisibility("ma_leave_review_v3");

  // Defensive page visibility map
  const pageVisibilityMap: Record<string, boolean> = {};
  try {
    pageVisibilityMap["/testimonials"] = testimonialsVisible;
    pageVisibilityMap["/leave-a-review"] = leaveReviewVisible;
  } catch { /* ignore */ }

  const isPageVisible = (page: (typeof STANDALONE_PAGES)[0]): boolean => {
    if (page.alwaysVisible) return true;
    if (page.visibilityKey) return pageVisibilityMap[page.path] ?? false;
    return true;
  };

  const togglePageVisibility = (page: (typeof STANDALONE_PAGES)[0]) => {
    if (!page.visibilityKey) return;
    if (page.path === "/testimonials") setTestimonialsVisible(!testimonialsVisible);
    if (page.path === "/leave-a-review") setLeaveReviewVisible(!leaveReviewVisible);
  };

  const [customPages, setCustomPages] = useState<CustomPage[]>(() => {
    try { return loadCustomPages(); } catch { return []; }
  });

  const toggleCustomPageVisibility = (page: CustomPage) => {
    try {
      const updated = { ...page, visible: !page.visible, updatedAt: new Date().toISOString() };
      saveCustomPage(updated);
      setCustomPages(loadCustomPages());
    } catch (err) {
      console.error("Failed to toggle custom page visibility:", err);
    }
  };

  // Build home sections dynamically from the live sectionOrder — with fallbacks
  let homeSections: { key: string; label: string; anchor: string; idx: number }[] = [];
  try {
    const sectionLabelsRaw = (() => {
      try {
        const raw = localStorage.getItem("ma_section_labels");
        if (raw) return JSON.parse(raw) as Record<string, string>;
      } catch { /* ignore */ }
      return {} as Record<string, string>;
    })();
    homeSections = (Array.isArray(sectionOrder) ? sectionOrder : []).map((key, idx) => {
      let label = String(key);
      try {
        label = sectionLabelsRaw[key] || String(key);
      } catch { /* ignore */ }
      return { key: String(key), label, anchor: `#${String(key)}`, idx: idx + 1 };
    });
  } catch (err) {
    console.error("Failed to build homeSections:", err);
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className={`text-xl font-bold mb-1 ${headingColor}`} style={{ fontFamily: "'Playfair Display', serif" }}>
          Site Directory
        </h2>
        <p className={`text-sm ${subColor}`}>
          Every page and section on your website — with live visibility status. Toggle visibility here, then click Save in the bottom bar to sync to the cloud.
        </p>
      </div>

      {/* Pages overview */}
      <div className={`rounded-2xl border overflow-hidden ${cardBg}`}>
        <div className={`px-5 py-4 border-b ${divider} flex items-center justify-between`}>
          <h3 className={`font-bold text-sm ${headingColor}`}>
            <i className="ri-pages-line text-pink-400 mr-1.5"></i>
            All Pages ({STANDALONE_PAGES.length})
          </h3>
        </div>
        <div className="divide-y" style={{ borderColor: isDark ? "#374151" : "#f3f4f6" }}>
          {STANDALONE_PAGES.map((page) => {
            const visible = isPageVisible(page);
            return (
              <div key={page.path} className={`flex items-center gap-4 px-5 py-4 transition-colors ${rowHover}`}>
                <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${isDark ? "bg-gray-700" : "bg-gray-100"}`}>
                  <i className={`${page.icon} text-pink-500 text-base`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-semibold text-sm ${headingColor}`}>{page.label}</div>
                  <div className={`text-xs font-mono mt-0.5 ${subColor}`}>{page.path}</div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                    visible
                      ? isDark ? "bg-green-900/40 text-green-400" : "bg-green-50 text-green-600"
                      : isDark ? "bg-gray-700 text-gray-500" : "bg-gray-100 text-gray-400"
                  }`}>
                    {visible ? "Live" : "Hidden"}
                  </span>
                  {page.visibilityKey && (
                    <button
                      type="button"
                      onClick={() => togglePageVisibility(page)}
                      className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${visible ? "bg-green-500" : isDark ? "bg-gray-600" : "bg-gray-300"}`}
                      title={visible ? "Hide this page" : "Show this page"}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${visible ? "translate-x-5" : "translate-x-0.5"}`}></span>
                    </button>
                  )}
                  <button
                    onClick={() => navigate(page.path)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                      isDark ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    <i className="ri-external-link-line mr-1"></i>Visit
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <div className={`px-5 py-3 border-t ${divider} ${isDark ? "bg-gray-700/30" : "bg-gray-50"}`}>
          <p className={`text-xs ${subColor}`}>
            <i className="ri-information-line mr-1"></i>
            Toggle visibility above, then click <strong className={headingColor}>Save</strong> in the bottom bar to sync changes to Supabase.
          </p>
        </div>
      </div>

      {/* Custom pages */}
      <div className={`rounded-2xl border overflow-hidden ${cardBg}`}>
        <div className={`px-5 py-4 border-b ${divider} flex items-center justify-between`}>
          <h3 className={`font-bold text-sm ${headingColor}`}>
            <i className="ri-file-add-line text-pink-400 mr-1.5"></i>
            Custom Pages ({customPages.length})
          </h3>
          <button
            onClick={() => navigate("/admin")}
            className={`text-xs flex items-center gap-1 cursor-pointer transition-colors ${isDark ? "text-gray-400 hover:text-white" : "text-gray-400 hover:text-gray-700"}`}
          >
            <i className="ri-add-line"></i> Create Page
          </button>
        </div>
        {customPages.length === 0 ? (
          <div className="px-5 py-8 text-center">
            <p className={`text-sm ${subColor}`}>No custom pages yet. Go to <strong className={headingColor}>Make a Page</strong> tab to create one.</p>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: isDark ? "#374151" : "#f3f4f6" }}>
            {customPages.map((page) => (
              <div key={page.id} className={`flex items-center gap-4 px-5 py-4 transition-colors ${rowHover}`}>
                <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${isDark ? "bg-gray-700" : "bg-gray-100"}`}>
                  <i className="ri-file-text-line text-pink-500 text-base"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-semibold text-sm ${headingColor}`}>{page.title || "Untitled Page"}</div>
                  <div className={`text-xs font-mono mt-0.5 ${subColor}`}>/{page.slug}</div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                    page.visible
                      ? isDark ? "bg-green-900/40 text-green-400" : "bg-green-50 text-green-600"
                      : isDark ? "bg-gray-700 text-gray-500" : "bg-gray-100 text-gray-400"
                  }`}>
                    {page.visible ? "Live" : "Hidden"}
                  </span>
                  <button
                    type="button"
                    onClick={() => toggleCustomPageVisibility(page)}
                    className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${page.visible ? "bg-green-500" : isDark ? "bg-gray-600" : "bg-gray-300"}`}
                    title={page.visible ? "Hide this page" : "Show this page"}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${page.visible ? "translate-x-5" : "translate-x-0.5"}`}></span>
                  </button>
                  <button
                    onClick={() => navigate(`/${page.slug}`)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${isDark ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                  >
                    <i className="ri-external-link-line mr-1"></i>Visit
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className={`px-5 py-3 border-t ${divider} ${isDark ? "bg-gray-700/30" : "bg-gray-50"}`}>
          <p className={`text-xs ${subColor}`}>
            <i className="ri-information-line mr-1"></i>
            Toggle visibility here or in the <strong className={headingColor}>Make a Page</strong> tab. Changes sync to Supabase on Save.
          </p>
        </div>
      </div>

      {/* Home page sections — DYNAMIC from sectionOrder */}
      <div className={`rounded-2xl border overflow-hidden ${cardBg}`}>
        <div className={`px-5 py-4 border-b ${divider}`}>
          <h3 className={`font-bold text-sm ${headingColor}`}>
            <i className="ri-layout-line text-pink-400 mr-1.5"></i>
            Home Page Sections ({homeSections.length})
          </h3>
          <p className={`text-xs mt-0.5 ${subColor}`}>Toggle visibility of each section. Hidden sections are completely invisible to visitors. Newly-added sections appear here automatically.</p>
        </div>
        <div className="divide-y" style={{ borderColor: isDark ? "#374151" : "#f3f4f6" }}>
          {homeSections.map((section) => {
            let visible = true;
            try { visible = isSectionVisible(section.key); } catch { /* ignore */ }
            return (
              <div key={section.key} className={`flex items-center gap-4 px-5 py-4 transition-colors ${rowHover}`}>
                <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 text-xs font-bold ${isDark ? "bg-gray-700 text-gray-400" : "bg-gray-100 text-gray-500"}`}>
                  {section.idx}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-semibold text-sm ${headingColor}`}>{section.label}</div>
                  <div className={`text-xs font-mono mt-0.5 ${subColor}`}>{section.anchor}</div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                    visible
                      ? isDark ? "bg-green-900/40 text-green-400" : "bg-green-50 text-green-600"
                      : isDark ? "bg-gray-700 text-gray-500" : "bg-gray-100 text-gray-400"
                  }`}>
                    {visible ? "Visible" : "Hidden"}
                  </span>
                  <button
                    type="button"
                    onClick={() => { try { toggleSection(section.key); } catch (err) { console.error(err); } }}
                    className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${visible ? "bg-green-500" : isDark ? "bg-gray-600" : "bg-gray-300"}`}
                    title={visible ? "Hide this section" : "Show this section"}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${visible ? "translate-x-5" : "translate-x-0.5"}`}></span>
                  </button>
                  <button
                    onClick={() => { navigate("/"); setTimeout(() => { document.querySelector(section.anchor)?.scrollIntoView({ behavior: "smooth" }); }, 500); }}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                      isDark ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    <i className="ri-edit-line mr-1"></i>Edit
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Other pages sections */}
      <div className={`rounded-2xl border overflow-hidden ${cardBg}`}>
        <div className={`px-5 py-4 border-b ${divider}`}>
          <h3 className={`font-bold text-sm ${headingColor}`}>
            <i className="ri-file-list-3-line text-pink-400 mr-1.5"></i>
            Other Page Sections
          </h3>
        </div>
        <div className="divide-y" style={{ borderColor: isDark ? "#374151" : "#f3f4f6" }}>
          {[
            { page: "Artists Page (/artists)", sections: ["Artist profiles grid", "Hero banner", "Apply CTA"] },
            { page: "Careers Page (/careers)", sections: ["Job listings", "Hero banner", "Application CTA"] },
            { page: "Testimonials (/testimonials)", sections: ["Featured testimonials (large cards)", "Regular testimonials grid", "Leave a Review CTA"] },
            { page: "Leave a Review (/leave-a-review)", sections: ["Review form", "Your Voice Matters sidebar", "See Reviews link"] },
          ].map((item) => (
            <div key={item.page} className={`px-5 py-4 transition-colors ${rowHover}`}>
              <div className={`font-semibold text-sm mb-2 ${headingColor}`}>{item.page}</div>
              <div className="flex flex-wrap gap-2">
                {item.sections.map((s) => (
                  <span key={s} className={`text-xs px-2.5 py-1 rounded-full ${isDark ? "bg-gray-700 text-gray-400" : "bg-gray-100 text-gray-500"}`}>
                    {s}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className={`px-5 py-3 border-t ${divider} ${isDark ? "bg-gray-700/30" : "bg-gray-50"}`}>
          <p className={`text-xs ${subColor}`}>
            <i className="ri-information-line mr-1"></i>
            Sections on these pages are edited inline — visit the page in admin mode and hover over any element to edit it.
          </p>
        </div>
      </div>
    </div>
  );
}